import { CivilLawSubjectType, RoleInCompanyType } from 'api/types/entity';
import { LocalFileEntity } from 'components/DocumentsTable/types';
import { subYears } from 'date-fns';
import { isValidName } from 'helpers';
import { isValidNumber } from 'libphonenumber-js';
import {
  ObjectSchema,
  string,
  object,
  FormErrors as FormErrorsBase,
  email,
  TypedForm,
  array,
  date,
  mixed,
} from 'libs/form';
import { TFunc } from 'libs/i18n';
import {
  getToday,
  getTodayWithResetHours,
  localFileScheme,
} from 'modules/client/common/helpers';
import { SelectType } from 'types/selectType';
import { Maybe } from 'yup';

export interface CreateLegalClientFormType {
  legal: {
    brightCode: string;
    fullName: string;
    personalAccountNumber: string;
    personalAccountOpeningDate: Date;
  };
  representative: {
    role: SelectType<RoleInCompanyType>;
    civilLawSubject?: Maybe<SelectType<CivilLawSubjectType>>;
    dateOfIssue: Date;
    dateOfExpiration?: Maybe<Date>;
    companyFullName?: Maybe<string>;
    email: string;
    firstName: string;
    lastName: string;
    middleName?: string;
    dateOfBirth: Date;
    phoneNumber: string;
  };

  companySignedClientServiceAgreementFiles: LocalFileEntity[];
  companySignedNoticeOfInvestmentAccountOpeningFiles: LocalFileEntity[];
}

export const dateOfBirthMaxDate = subYears(new Date(), 18);
export const dateOfBirthMinDate = subYears(new Date(), 100);

export const getSchema = (
  t: TFunc,
  { enabledCivilLawSubject }: { enabledCivilLawSubject: boolean }
): ObjectSchema<CreateLegalClientFormType> =>
  object({
    legal: object({
      brightCode: string().required(),
      fullName: string().max(200).required(),
      personalAccountNumber: string().max(200).required(),
      personalAccountOpeningDate: date().required(),
    }),

    representative: object({
      role: object({
        value: mixed<RoleInCompanyType>()
          .oneOf(Object.values(RoleInCompanyType))
          .required(),
        label: string().required(),
      })
        .required()
        .default(null),

      civilLawSubject: object({
        value: mixed<CivilLawSubjectType>()
          .oneOf(Object.values(CivilLawSubjectType))
          .required(),
        label: string().required(),
      })
        .default(null)
        .nullable()
        .when('role', {
          is: (v: SelectType<RoleInCompanyType>) =>
            enabledCivilLawSubject &&
            v?.value === RoleInCompanyType.AuthorizedRepresentative,
          then: (schema) => schema.required(),
        }),

      dateOfIssue: date().max(getToday(), t('validation.maxDate')).required(),
      dateOfExpiration: date()
        .min(getTodayWithResetHours(), t('validation.minDate'))
        .nullable(),

      companyFullName: string()
        .max(200)
        .default(null)
        .nullable()
        .when('civilLawSubject', {
          is: (v: SelectType<CivilLawSubjectType>) =>
            v?.value === CivilLawSubjectType.JuridicalPerson,
          then: (schema) => schema.required(),
        }),

      email: email(t).required(),

      firstName: string()
        .max(20)
        .required()
        .test('ischars', t('validation.noSpecialCharsAndNumber'), isValidName),
      lastName: string()
        .max(20)
        .required()
        .test('ischars', t('validation.noSpecialCharsAndNumber'), isValidName),
      middleName: string()
        .max(20)
        .test('ischars', t('validation.noSpecialCharsAndNumber'), isValidName),
      dateOfBirth: date()
        .min(
          dateOfBirthMinDate,
          t('client.createLegalClient.fields.dateOfBirth.dateOfBirthMinError')
        )
        .max(
          dateOfBirthMaxDate,
          t('client.createLegalClient.fields.dateOfBirth.dateOfBirthMaxError')
        )
        .required(),

      phoneNumber: string()
        .test('officerPhoneNumber', t('validation.phone'), (value) => {
          if (!value) return true;
          return isValidNumber(value);
        })
        .max(20)
        .required(),
    }),

    companySignedClientServiceAgreementFiles: array()
      .of(localFileScheme)
      .required()
      .min(1),
    companySignedNoticeOfInvestmentAccountOpeningFiles: array()
      .of(localFileScheme)
      .required()
      .min(1),
  });

export const { Field, Form, useFormContext, Submit } =
  TypedForm<CreateLegalClientFormType>();

export type FormErrors = FormErrorsBase<CreateLegalClientFormType>;
